import { useCallback, useMemo, useEffect } from "react";
import { cn } from "@hireart/component-library";
import PropTypes from "prop-types";
import { Drawer, DrawerContent, DrawerTrigger } from "common/components/Drawer";
import {
  ShiftShape,
  ProjectOptionShape,
  TimezoneShape,
  isWeekReadOnly,
  getValidationErrorMessages,
} from "common/utils/timesheet";
import { useIsMobile } from "./hooks/useIsMobile";
import { useShiftState } from "./hooks/useShiftState";
import { ShiftHeader, ShiftContent, ShiftFooter } from "./sub-components";

export const ShiftDetail = ({
  shift,
  shiftDate,
  dayTotalHours = 0,
  weekStatus = "draft",
  projectOptions = [],
  timezones = [],
  onSave,
  onDelete,
  children,
  isSaving = false,
  isOpen = false,
  onOpenChange,
  overrideReasonRequired = false,
}) => {
  const isMobile = useIsMobile();
  const isReadOnly = isWeekReadOnly(weekStatus);

  // Custom hooks for state management - now works with ShiftData
  const {
    editingShift,
    selectedProjectToken,
    updateShift,
    updateShiftTime,
    updateBreak,
    addBreak,
    removeBreak,
    updateProject,
    updateTimezone,
    getCurrentTimezone,
    resetShift,
  } = useShiftState({
    initialShift: shift,
    shiftDate,
    isReadOnly,
  });

  // Reset form when drawer opens - ensures clean slate every time
  useEffect(() => {
    if (isOpen) {
      resetShift();
    }
  }, [isOpen, resetShift]);

  // Validate shift data whenever it changes
  const validationErrors = useMemo(
    () => getValidationErrorMessages(editingShift),
    [editingShift]
  );

  // Event handlers
  const handleSave = () => {
    // Validation errors are shown in real-time, so we can always attempt to save
    // The backend will also validate, but we show front-end errors immediately

    // Call the onSave callback with the updated shift
    onSave(editingShift);
  };
  const handleDelete = () => {
    if (isReadOnly) return;
    if (shift.id) {
      onDelete(shift.id);
    }
  };

  // Memoized callback functions to prevent unnecessary re-renders
  const handleStartTimeChange = useCallback(
    (time) => updateShiftTime("startTime", time),
    [updateShiftTime]
  );
  const handleEndTimeChange = useCallback(
    (time) => updateShiftTime("endTime", time),
    [updateShiftTime]
  );
  const handleBreakChange = useCallback(
    (breakIndex, field, value) => {
      updateBreak(breakIndex, field, value);
    },
    [updateBreak]
  );
  const handleNotesChange = useCallback(
    (notes) =>
      updateShift({
        notes,
      }),
    [updateShift]
  );
  const handleOverrideNotesChange = useCallback(
    (overrideNotes) =>
      updateShift({
        overrideNotes,
      }),
    [updateShift]
  );
  const handleReferenceNumberChange = useCallback(
    (referenceNumber) =>
      updateShift({
        referenceNumber,
      }),
    [updateShift]
  );
  const isExistingShift = Boolean(shift.id);
  const drawerDirection = isMobile ? "bottom" : "right";
  const drawerContentClassName = isMobile
    ? "h-[85vh] max-h-[85vh] min-h-[60vh] bg-neutral-10"
    : "h-screen bg-neutral-10 inset-x-auto right-0 w-full max-w-xl";
  const drawerContentStyle = isMobile
    ? {
        height:
          "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 6rem)",
        maxHeight:
          "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 6rem)",
        top: "env(safe-area-inset-top)",
        bottom: "env(safe-area-inset-bottom)",
      }
    : {};
  return (
    <Drawer
      direction={drawerDirection}
      open={isOpen}
      onOpenChange={onOpenChange}
      // Vaul's default repositionInputs=true causes an infinite loop on iOS Safari:
      // focusing an input → keyboard opens → visualViewport resize → Vaul resizes
      // the drawer height → triggers another visualViewport resize → loop.
      // Disabling it lets iOS handle keyboard scroll-into-view natively instead.
      repositionInputs={false}
    >
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent
        className={cn(drawerContentClassName, "body-1")}
        style={drawerContentStyle}
      >
        <ShiftHeader
          shift={isReadOnly ? shift : editingShift}
          shiftDate={shiftDate}
          weekStatus={weekStatus}
        />

        <ShiftContent
          shift={shift}
          editingShift={editingShift}
          shiftDate={shiftDate}
          dayTotalHours={dayTotalHours}
          selectedProjectToken={selectedProjectToken}
          projectOptions={projectOptions}
          timezones={timezones}
          validationErrors={validationErrors}
          isReadOnly={isReadOnly}
          onProjectChange={updateProject}
          onStartTimeChange={handleStartTimeChange}
          onEndTimeChange={handleEndTimeChange}
          onTimezoneChange={updateTimezone}
          onBreakChange={handleBreakChange}
          onRemoveBreak={removeBreak}
          onAddBreak={addBreak}
          onNotesChange={handleNotesChange}
          onReferenceNumberChange={handleReferenceNumberChange}
          getCurrentTimezone={getCurrentTimezone}
          overrideReasonRequired={overrideReasonRequired}
          overrideNotes={editingShift.overrideNotes || ""}
          onOverrideNotesChange={handleOverrideNotesChange}
        />

        <ShiftFooter
          isReadOnly={isReadOnly}
          isMobile={isMobile}
          onSave={handleSave}
          onDelete={isExistingShift ? handleDelete : undefined}
          isSaving={isSaving}
          isExistingShift={isExistingShift}
          isSaveDisabled={
            overrideReasonRequired && !editingShift.overrideNotes?.trim()
          }
        />
      </DrawerContent>
    </Drawer>
  );
};

ShiftDetail.propTypes = {
  shift: ShiftShape.isRequired,
  shiftDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
    .isRequired,
  dayTotalHours: PropTypes.number,
  weekStatus: PropTypes.string,
  projectOptions: PropTypes.arrayOf(ProjectOptionShape),
  timezones: PropTypes.arrayOf(TimezoneShape),
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  isSaving: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func.isRequired,
  overrideReasonRequired: PropTypes.bool,
};
